import { useState, useEffect, useCallback } from 'react';
import { X, Search, Check, Loader2 } from 'lucide-react';
import { searchIssues, updateIssue } from '../../services/issueService';
import type { SearchIssueResult } from '../../services/issueService';
import { validateHierarchy } from '../../utils/issueHierarchy';
import { useQueryClient } from '@tanstack/react-query';
import { issueKeys } from '../../hooks/useIssues';
import toast from 'react-hot-toast';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';

interface IssueLinkModalProps {
  currentIssue: {
    id: string;           // cuid (for API URL param)
    issueId: string;      // business key e.g. "TF-1" (for parentIssueId value)
    issueType: string;
    parentIssueId: string | null;
  };
  projectId: string;
  onSuccess: () => void;
  onClose: () => void;
}

export default function IssueLinkModal({
  currentIssue,
  projectId,
  onSuccess,
  onClose,
}: IssueLinkModalProps) {
  const [linkType, setLinkType] = useState<'parent' | 'child'>('child');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchIssueResult[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<SearchIssueResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const queryClient = useQueryClient();

  // 디바운스 검색
  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchIssues(projectId, searchQuery.trim(), currentIssue.issueId);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, projectId, currentIssue.issueId]);

  // 연결 유형 변경 시 선택 초기화
  const handleLinkTypeChange = (type: 'parent' | 'child') => {
    setLinkType(type);
    setSelectedIssue(null);
  };

  // 계층 규칙 검증
  const getValidation = useCallback(
    (candidate: SearchIssueResult): { valid: boolean; message?: string } => {
      if (linkType === 'parent') {
        // 선택 이슈가 현재 이슈의 상위가 됨
        return validateHierarchy(currentIssue.issueType, candidate.issueType);
      } else {
        // 선택 이슈가 현재 이슈의 하위가 됨
        return validateHierarchy(candidate.issueType, currentIssue.issueType);
      }
    },
    [linkType, currentIssue.issueType],
  );

  // 연결 실행
  const handleLink = async () => {
    if (!selectedIssue || submitting) return;

    // 기존 상위 이슈 교체 경고
    if (linkType === 'parent' && currentIssue.parentIssueId) {
      setConfirmMessage(
        `현재 상위 작업(${currentIssue.parentIssueId})과의 연결이 해제되고 ${selectedIssue.issueId}(이)가 새로운 상위 작업으로 설정됩니다. 계속하시겠습니까?`,
      );
      setShowConfirm(true);
      return;
    }
    if (linkType === 'child' && selectedIssue.parentIssueId) {
      setConfirmMessage(
        `${selectedIssue.issueId}의 기존 상위 작업(${selectedIssue.parentIssueId})과의 연결이 해제됩니다. 계속하시겠습니까?`,
      );
      setShowConfirm(true);
      return;
    }

    await executeLink();
  };

  const executeLink = async () => {
    if (!selectedIssue) return;
    setSubmitting(true);
    setShowConfirm(false);

    try {
      if (linkType === 'parent') {
        // 현재 이슈의 parentIssueId를 선택 이슈의 business key로 설정
        await updateIssue(projectId, currentIssue.id, {
          parentIssueId: selectedIssue.issueId,
        });
      } else {
        // 선택 이슈의 parentIssueId를 현재 이슈의 business key로 설정
        await updateIssue(projectId, selectedIssue.id, {
          parentIssueId: currentIssue.issueId,
        });
      }

      // React Query 캐시 무효화
      queryClient.invalidateQueries({ queryKey: issueKeys.all(projectId) });

      toast.success('작업이 연결되었습니다.');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error.response?.data?.message || '작업 연결에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-[500px] max-h-[600px] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-lg font-semibold">작업 연결하기</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 flex-1 overflow-y-auto space-y-4">
          {/* 연결 유형 선택 */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">연결 유형</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="linkType"
                checked={linkType === 'parent'}
                onChange={() => handleLinkTypeChange('parent')}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">상위 작업으로 연결</span>
              <span className="text-sm text-gray-400">— 선택한 작업이 현재 작업의 상위가 됩니다</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="linkType"
                checked={linkType === 'child'}
                onChange={() => handleLinkTypeChange('child')}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">하위 작업으로 연결</span>
              <span className="text-sm text-gray-400">— 선택한 작업이 현재 작업의 하위가 됩니다</span>
            </label>
          </div>

          {/* 이슈 검색 */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">작업 검색</p>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="작업 ID 또는 제목으로 검색..."
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                autoFocus
              />
            </div>
          </div>

          {/* 검색 결과 */}
          {searching && (
            <div className="flex justify-center py-4">
              <Loader2 className="size-5 animate-spin text-gray-400" />
            </div>
          )}

          {!searching && searchResults.length > 0 && (
            <div className="border rounded-lg divide-y max-h-[240px] overflow-y-auto">
              {searchResults.map((result) => {
                const validation = getValidation(result);
                const isSelected = selectedIssue?.id === result.id;
                const typeConf = resolveIssueTypeConfig(undefined, result.issueType);

                return (
                  <button
                    key={result.id}
                    onClick={() => validation.valid && setSelectedIssue(result)}
                    disabled={!validation.valid}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${
                      !validation.valid
                        ? 'bg-gray-50 opacity-50 cursor-not-allowed'
                        : isSelected
                          ? 'bg-blue-50 ring-1 ring-blue-300'
                          : 'hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <typeConf.Icon size={14} className={`${typeConf.color} flex-shrink-0`} style={typeConf.hexColor ? { color: typeConf.hexColor } : undefined} />
                      <span className="text-sm text-gray-400 font-mono flex-shrink-0">{result.issueId}</span>
                      <span className="text-sm truncate">{result.title}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {!validation.valid ? (
                        <span className="text-sm text-red-400">{validation.message}</span>
                      ) : (
                        <span className="text-sm text-gray-400">{result.issueType}</span>
                      )}
                      {isSelected && <Check size={14} className="text-blue-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {!searching && searchQuery.trim() && searchResults.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">검색 결과가 없습니다.</p>
          )}
        </div>

        {/* 확인 경고 (기존 상위 교체 시) */}
        {showConfirm && (
          <div className="px-6 pb-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">{confirmMessage}</p>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={executeLink}
                  className="px-3 py-1.5 text-sm text-white bg-amber-600 rounded hover:bg-amber-700"
                >
                  계속
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 하단 버튼 */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleLink}
            disabled={!selectedIssue || submitting}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting ? '연결 중...' : '연결'}
          </button>
        </div>
      </div>
    </div>
  );
}
